import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { fetchThemeData } from '../../themeSlice';
import { selectAdminToken } from '../../sessionSlice';
import { apiUrl } from '../../../utils/apiUrl';
import { getActiveGame, getGameDisplayName, resolveGameIconUrl } from '../../../utils/gameHelpers';
import './gameBranding.css';

const FIELDS = [
  {
    key: 'game_logo',
    label: 'Logo',
    hint: '400 × 300 px',
    dimensions: [400, 300],
    themeKey: 'logo',
    themeFallback: 'logo.png',
  },
  {
    key: 'game_background_desk',
    label: 'Desktop background',
    hint: '1920 × 1080 px',
    dimensions: [1920, 1080],
    themeKey: 'background_desk',
    themeFallback: 'desktop.jpg',
  },
  {
    key: 'game_background_mob',
    label: 'Mobile background',
    hint: '414 × 896 px',
    dimensions: [414, 896],
    themeKey: 'background_mob',
    themeFallback: 'mobile.jpg',
  },
];

const VALID_EXT = ['jpg', 'jpeg', 'png'];
const MAX_MB = 5;

function previewUrl(filename) {
  const f = String(filename || '').trim();
  if (!f) return '';
  return resolveGameIconUrl(f);
}

function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

export default function GameBrandingSection() {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((s) => s.theme);
  const activeGame = getActiveGame(data);
  const gameName = getGameDisplayName(activeGame);

  const [busyKey, setBusyKey] = useState('');
  const [localPreviews, setLocalPreviews] = useState({});
  const inputsRef = useRef({});

  useEffect(() => {
    setLocalPreviews({});
  }, [activeGame, currentTheme]);

  const themeFile = (field) => {
    const raw =
      data?.[field.key] ||
      data?.[field.themeKey] ||
      (field.themeKey === 'background_desk' ? data?.backgroundDesk : null) ||
      (field.themeKey === 'background_mob' ? data?.backgroundMob : null) ||
      field.themeFallback;
    return String(raw || '').trim();
  };

  const customFile = (field) => String(data?.[field.key] || '').trim();

  const displayFile = (field) => localPreviews[field.key] || previewUrl(themeFile(field));

  const isCustom = (field) => Boolean(customFile(field));

  const upload = async (field, file) => {
    if (!adminToken || !currentTheme) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!VALID_EXT.includes(ext)) {
      Swal.fire('Invalid file', `Only ${VALID_EXT.join(', ').toUpperCase()} allowed.`, 'error');
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      Swal.fire('File too large', `Maximum ${MAX_MB} MB.`, 'error');
      return;
    }

    try {
      const { width, height } = await getImageDimensions(file);
      const [rw, rh] = field.dimensions;
      if (width !== rw || height !== rh) {
        Swal.fire(
          'Wrong size',
          `${field.label} must be exactly ${rw}×${rh}px (got ${width}×${height}).`,
          'error'
        );
        return;
      }
    } catch {
      Swal.fire('Error', 'Could not read image dimensions.', 'error');
      return;
    }

    setBusyKey(field.key);
    setLocalPreviews((prev) => ({ ...prev, [field.key]: URL.createObjectURL(file) }));

    const fd = new FormData();
    fd.append('file', file);
    fd.append('target', field.key);
    fd.append('themeName', String(currentTheme));

    try {
      const res = await fetch(apiUrl('/uploadGameBranding'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Upload failed');
      }
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      setLocalPreviews((prev) => {
        const next = { ...prev };
        delete next[field.key];
        return next;
      });
      Swal.fire({
        icon: 'success',
        title: 'Uploaded',
        text: `${field.label} saved for ${gameName}.`,
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err) {
      setLocalPreviews((prev) => {
        const next = { ...prev };
        delete next[field.key];
        return next;
      });
      Swal.fire('Error', err.message || 'Upload failed.', 'error');
    } finally {
      setBusyKey('');
      if (inputsRef.current[field.key]) inputsRef.current[field.key].value = '';
    }
  };

  const clearCustom = async (field) => {
    if (!isCustom(field)) return;
    const confirm = await Swal.fire({
      title: `Reset ${field.label}?`,
      text: `This game will use the theme default ${field.label.toLowerCase()} again.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Use default',
    });
    if (!confirm.isConfirmed) return;

    setBusyKey(field.key);
    const fd = new FormData();
    fd.append('target', field.key);
    fd.append('themeName', String(currentTheme));
    fd.append('clear', 'true');
    // express-fileupload still needs a body; empty file optional
    try {
      const res = await fetch(apiUrl('/uploadGameBranding'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) throw new Error(json.error || 'Reset failed');
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire({
        icon: 'success',
        title: 'Reset',
        text: `${field.label} now uses the theme default.`,
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire('Error', err.message || 'Reset failed.', 'error');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div className="game-branding">
      <div className="game-branding-head">
        <h3 className="game-branding-title">
          <i className="fa-solid fa-image" /> Game branding — {gameName}
        </h3>
        <p className="game-branding-desc">
          Optional logo and backgrounds for this game only. If empty, the theme default images are
          used.
        </p>
      </div>

      <div className="game-branding-grid">
        {FIELDS.map((field) => {
          const src = displayFile(field);
          const custom = isCustom(field);
          const busy = busyKey === field.key;
          return (
            <div className="game-branding-card" key={field.key}>
              <div className="game-branding-card-top">
                <span className="game-branding-label">{field.label}</span>
                <span className={`game-branding-badge${custom ? ' is-custom' : ''}`}>
                  {custom ? 'Custom' : 'Default'}
                </span>
              </div>
              <p className="game-branding-hint">{field.hint} · JPG/PNG · max {MAX_MB} MB</p>

              <button
                type="button"
                className={`game-branding-preview game-branding-preview--${field.key}`}
                onClick={() => inputsRef.current[field.key]?.click()}
                disabled={busy}
                aria-label={`Upload ${field.label}`}
              >
                {src ? (
                  <img src={src} alt="" referrerPolicy="no-referrer" />
                ) : (
                  <span className="game-branding-empty">
                    <i className="fa-solid fa-cloud-arrow-up" />
                    Upload
                  </span>
                )}
                {busy ? <span className="game-branding-busy">Saving…</span> : null}
              </button>

              <div className="game-branding-actions">
                <button
                  type="button"
                  className="game-branding-btn"
                  disabled={busy}
                  onClick={() => inputsRef.current[field.key]?.click()}
                >
                  {custom ? 'Replace' : 'Upload'}
                </button>
                <button
                  type="button"
                  className="game-branding-btn game-branding-btn--danger"
                  disabled={busy || !custom}
                  onClick={() => clearCustom(field)}
                >
                  Use default
                </button>
              </div>

              <input
                ref={(el) => {
                  inputsRef.current[field.key] = el;
                }}
                type="file"
                accept="image/jpeg,image/png"
                className="game-branding-input"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) upload(field, file);
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
